import { useEffect, useRef, useState } from "react";

import { readLine, withDerivedRequirements } from "./reviewLineGates";

export const LINE_EDIT_DEBOUNCE_MS = 500;

const isSameValue = (first, second) =>
  JSON.stringify(first) === JSON.stringify(second);

const omitFieldsMatching = (localFields, isMatch) => {
  const remaining = Object.fromEntries(
    Object.entries(localFields).filter(
      ([field, value]) => !isMatch(field, value)
    )
  );
  return Object.keys(remaining).length === Object.keys(localFields).length
    ? localFields
    : remaining;
};

// One line's review draft. Edits show immediately (localFields) and persist
// through update-lines, debounced 500ms per line, so closing the dialog
// halfway loses nothing (PRD §8.5). Discrete actions (acknowledge, allocate,
// reject) skip the debounce via saveNow. An optimistic value stays until the
// line's snapshot carries it; a rejected save drops it (unless the reviewer
// has typed something newer) so the field falls back to the server's value.
export const useSmartUploadLineDraft = ({
  line,
  documentFamily,
  lineKey,
  saveFields,
  onPendingChange
}) => {
  const [localFields, setLocalFields] = useState({});
  const [pendingFields, setPendingFields] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const saveFieldsRef = useRef(saveFields);
  const onPendingChangeRef = useRef(onPendingChange);
  useEffect(() => {
    saveFieldsRef.current = saveFields;
    onPendingChangeRef.current = onPendingChange;
  });

  const sendFields = async (fields) => {
    setIsSaving(true);
    setSaveError(null);
    const response = await saveFieldsRef.current(fields);
    setIsSaving(false);

    if (!response || response.error) {
      setLocalFields((current) =>
        omitFieldsMatching(
          current,
          (field, value) => field in fields && isSameValue(value, fields[field])
        )
      );
      setSaveError(
        response?.message || "We couldn't save that change — please try again."
      );
    }
    return response;
  };

  useEffect(() => {
    setLocalFields((current) =>
      omitFieldsMatching(current, (field, value) =>
        isSameValue(value, line[field] ?? null)
      )
    );
  }, [line]);

  useEffect(() => {
    if (!pendingFields) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      setPendingFields(null);
      sendFields(pendingFields);
    }, LINE_EDIT_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // sendFields reads its callbacks through refs, so only a new edit restarts
    // this line's debounce -- never another line's snapshot re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFields]);

  useEffect(() => {
    onPendingChangeRef.current(lineKey, !!pendingFields || isSaving);
  }, [lineKey, pendingFields, isSaving]);

  useEffect(() => {
    const notifyPendingChange = onPendingChangeRef.current;
    return () => notifyPendingChange(lineKey, false);
  }, [lineKey]);

  const editFields = (fields) => {
    setLocalFields((current) => ({ ...current, ...fields }));
    setPendingFields((current) => ({ ...(current || {}), ...fields }));
  };

  const saveNow = async (fields = {}) => {
    const combinedFields = { ...(pendingFields || {}), ...fields };
    setPendingFields(null);
    setLocalFields((current) => ({ ...current, ...fields }));
    if (!Object.keys(combinedFields).length) {
      return null;
    }
    return sendFields(combinedFields);
  };

  const draftLine = withDerivedRequirements(
    readLine({ ...line, ...localFields }),
    documentFamily
  );

  return {
    draftLine,
    editFields,
    saveNow,
    flushNow: () => saveNow({}),
    isSaving,
    isPending: !!pendingFields,
    saveError
  };
};
